import { create } from "zustand";

import {
  applySidebarChange,
  availableSidebarEntries,
} from "@/lib/sidebar-session-transitions";

import {
  type SidebarChange,
  type SidebarDefinition,
  type SidebarEnvironment,
  type SidebarOperation,
  type SidebarRegistration,
  type SidebarSnapshot,
  type SidebarStrategyContext,
  eraseSidebar,
  readSidebar,
} from "@/types/sidebar-session";

import { onTenantChange } from "./tenant-store";

export { defineSidebar, readSidebar } from "@/types/sidebar-session";
export const RIGHT_SIDEBAR_DEFAULT_WIDTH = 352;
export const RIGHT_SIDEBAR_MIN_WIDTH = 320;
export const RIGHT_SIDEBAR_MAX_WIDTH = 640;
const STORAGE_KEY = "right-sidebar-width";

function getPersistedWidth(): number {
  if (typeof window === "undefined") return RIGHT_SIDEBAR_DEFAULT_WIDTH;
  try {
    const parsed = Number(localStorage.getItem(STORAGE_KEY));
    return Number.isFinite(parsed) &&
      parsed >= RIGHT_SIDEBAR_MIN_WIDTH &&
      parsed <= RIGHT_SIDEBAR_MAX_WIDTH
      ? parsed
      : RIGHT_SIDEBAR_DEFAULT_WIDTH;
  } catch {
    return RIGHT_SIDEBAR_DEFAULT_WIDTH;
  }
}

export interface RightSidebarState extends SidebarSnapshot {
  width: number;
  /** Registration lifetime identity, not a second membership authority. */
  scopeToken: symbol;
  open<I, P>(definition: SidebarDefinition<I, P>, input: I): void;
  close(key: string): void;
  select(key: string): void;
  closeAll(): void;
  register<I, P>(
    definition: SidebarDefinition<I, P>,
    owner?: symbol,
  ): SidebarRegistration;
  unregister(handle: SidebarRegistration): void;
  updateEnvironment(environment: SidebarEnvironment): void;
  beginOperation(key: string): SidebarOperation;
  setWidth(width: number): void;
}

const entryCache = new WeakMap<
  SidebarSnapshot,
  ReturnType<typeof availableSidebarEntries>
>();
export function selectSidebarEntries(state: SidebarSnapshot) {
  let entries = entryCache.get(state);
  if (!entries) {
    entries = availableSidebarEntries(state);
    entryCache.set(state, entries);
  }
  return entries;
}
export function selectSidebarOpen(state: SidebarSnapshot): boolean {
  return selectSidebarEntries(state).length > 0;
}

export function createRightSidebarStore() {
  let revision = 0;
  const operations = new Map<string, AbortController>();
  const cancelOperation = (key: string) => {
    const operation = operations.get(key);
    operations.delete(key);
    operation?.abort();
  };
  const cancelAllOperations = () => {
    const pending = [...operations.values()];
    operations.clear();
    pending.forEach((operation) => operation.abort());
  };
  const context = (snapshot: SidebarSnapshot): SidebarStrategyContext => ({
    snapshot,
    read: (definition) => readSidebar(snapshot, definition),
    write: (definition, payload) => ({
      definition: eraseSidebar(definition),
      entry: { key: definition.id, payload, revision: ++revision },
    }),
  });

  return create<RightSidebarState>((set, get) => {
    const commit = (change: SidebarChange, snapshot = get()) => {
      if (
        !change.upsert?.length &&
        !change.remove?.length &&
        change.select === undefined
      ) {
        change.effects?.forEach((effect) => effect());
        return;
      }
      const next = applySidebarChange(snapshot, change);
      const removed = Object.keys(snapshot.entries).filter(
        (key) => !next.entries[key],
      );
      // An explicit removal also dismisses a pending opening with no entry yet.
      // Invalidate before subscribers can complete obsolete work.
      new Set([...removed, ...(change.remove ?? [])]).forEach(cancelOperation);
      if (
        change.upsert?.length ||
        removed.length ||
        next.activeKey !== snapshot.activeKey ||
        snapshot.registrations !== get().registrations
      ) {
        set({
          entries: next.entries,
          registrations: next.registrations,
          activeKey: next.activeKey,
        });
      }
      change.effects?.forEach((effect) => effect());
    };
    const closePlan = (
      snapshot: SidebarSnapshot,
      key: string,
    ): SidebarChange => {
      const entry = snapshot.entries[key];
      const definition = snapshot.registrations[key]?.definition;
      const change =
        entry && definition?.strategy.planClose
          ? definition.strategy.planClose(context(snapshot), entry)
          : {};
      return {
        ...change,
        upsert: change.upsert?.filter((write) => write.entry.key !== key),
        remove: [...new Set([key, ...(change.remove ?? [])])],
      };
    };
    const cleanupPlans = (snapshot: SidebarSnapshot) => {
      const effects: (() => void)[] = [];
      // Every originally open panel owns cleanup, even when another strategy's
      // plan removes a related entry. closeAll never applies their fallback plans.
      for (const key of Object.keys(snapshot.entries)) {
        effects.push(...(closePlan(snapshot, key).effects ?? []));
      }
      return effects;
    };
    return {
      entries: {},
      registrations: {},
      activeKey: null,
      environment: { ownerScope: null, path: "", capabilities: {} },
      scopeToken: Symbol("SIDEBAR_SCOPE"),
      width: getPersistedWidth(),
      open: (definition, input) => {
        const snapshot = get();
        const registered = snapshot.registrations[definition.id]?.definition;
        if (
          (registered ?? definition).isAvailable?.(snapshot.environment) ===
          false
        )
          return;
        commit(
          definition.strategy.planOpen(context(snapshot), input),
          snapshot,
        );
      },
      close: (key) => {
        cancelOperation(key);
        const snapshot = get();
        if (!snapshot.entries[key]) return;
        commit(closePlan(snapshot, key), snapshot);
      },
      select: (key) => {
        const snapshot = get();
        if (
          snapshot.activeKey === key ||
          !selectSidebarEntries(snapshot).some((entry) => entry.key === key)
        )
          return;
        set({ activeKey: key });
      },
      closeAll: () => {
        const snapshot = get();
        const effects = cleanupPlans(snapshot);
        cancelAllOperations();
        if (
          Object.keys(snapshot.entries).length > 0 ||
          snapshot.activeKey !== null
        ) {
          set({ entries: {}, activeKey: null });
        }
        effects.forEach((effect) => effect());
      },
      register: (definition, owner = Symbol(definition.id)) => {
        const state = get();
        const existing = state.registrations[definition.id];
        const owners = new Map(existing?.owners);
        const erased = eraseSidebar(definition);
        if (existing?.owners.get(owner) === erased) {
          return {
            key: definition.id,
            owner,
            scope: state.environment.ownerScope,
            scopeToken: state.scopeToken,
          };
        }
        owners.set(owner, erased);
        // Updating an older owner does not steal a newer owner's lifetime.
        const winner = [...owners.values()].at(-1)!;
        const registration = { definition: winner, owners, implicit: false };
        const snapshot = {
          ...state,
          registrations: {
            ...state.registrations,
            [definition.id]: registration,
          },
        };
        if (
          state.entries[definition.id] &&
          winner.isAvailable?.(state.environment) === false
        ) {
          commit(closePlan(snapshot, definition.id), snapshot);
        } else {
          set({ registrations: snapshot.registrations });
        }
        return {
          key: definition.id,
          owner,
          scope: state.environment.ownerScope,
          scopeToken: state.scopeToken,
        };
      },
      unregister: (handle) => {
        const state = get();
        if (handle.scopeToken !== state.scopeToken) return;
        const registered = state.registrations[handle.key];
        if (!registered?.owners.has(handle.owner)) return;
        const owners = new Map(registered.owners);
        owners.delete(handle.owner);
        const registrations = { ...state.registrations };
        if (owners.size > 0) {
          registrations[handle.key] = {
            ...registered,
            owners,
            definition: [...owners.values()].at(-1)!,
          };
          const snapshot = { ...state, registrations };
          if (
            registrations[handle.key].definition.isAvailable?.(
              state.environment,
            ) === false
          ) {
            commit(closePlan(snapshot, handle.key), snapshot);
          } else {
            const normalized = applySidebarChange(snapshot, {});
            set({
              registrations,
              entries: normalized.entries,
              activeKey: normalized.activeKey,
            });
          }
          return;
        }
        const plan = closePlan(state, handle.key);
        const next = applySidebarChange(state, plan);
        const nextRegistrations = { ...next.registrations };
        delete nextRegistrations[handle.key];
        const normalized = applySidebarChange(
          { ...next, registrations: nextRegistrations },
          {},
        );
        const removed = Object.keys(state.entries).filter(
          (key) => !normalized.entries[key],
        );
        new Set([handle.key, ...removed, ...(plan.remove ?? [])]).forEach(
          cancelOperation,
        );
        set({
          entries: normalized.entries,
          registrations: nextRegistrations,
          activeKey: normalized.activeKey,
        });
        plan.effects?.forEach((effect) => effect());
      },
      updateEnvironment: (environment) => {
        const state = get();
        if (state.environment.ownerScope !== environment.ownerScope) {
          const effects = cleanupPlans(state);
          cancelAllOperations();
          set({
            environment,
            entries: {},
            registrations: {},
            activeKey: null,
            scopeToken: Symbol("SIDEBAR_SCOPE"),
          });
          effects.forEach((effect) => effect());
          return;
        }
        if (
          state.environment.path === environment.path &&
          Object.keys(environment.capabilities).length ===
            Object.keys(state.environment.capabilities).length &&
          Object.entries(environment.capabilities).every(
            ([key, value]) => state.environment.capabilities[key] === value,
          )
        )
          return;
        let snapshot: SidebarSnapshot = { ...state, environment };
        const effects: (() => void)[] = [];
        for (const entry of Object.values(state.entries)) {
          const definition = state.registrations[entry.key]?.definition;
          if (definition?.isAvailable?.(environment) !== false) continue;
          const plan = closePlan(snapshot, entry.key);
          effects.push(...(plan.effects ?? []));
          snapshot = applySidebarChange(snapshot, plan);
          cancelOperation(entry.key);
        }
        const normalized = applySidebarChange(snapshot, {});
        set({
          environment,
          entries: normalized.entries,
          registrations: normalized.registrations,
          activeKey: normalized.activeKey,
        });
        effects.forEach((effect) => effect());
      },
      beginOperation: (key) => {
        cancelOperation(key);
        const controller = new AbortController();
        const scope = get().scopeToken;
        operations.set(key, controller);
        return {
          signal: controller.signal,
          isCurrent: () =>
            !controller.signal.aborted &&
            operations.get(key) === controller &&
            get().scopeToken === scope,
          dispose: () => {
            if (operations.get(key) === controller) operations.delete(key);
            controller.abort();
          },
        };
      },
      setWidth: (width) => {
        if (!Number.isFinite(width)) return;
        const clamped = Math.min(
          RIGHT_SIDEBAR_MAX_WIDTH,
          Math.max(RIGHT_SIDEBAR_MIN_WIDTH, width),
        );
        try {
          if (typeof window !== "undefined")
            localStorage.setItem(STORAGE_KEY, String(clamped));
        } catch {
          // Storage may be blocked; resizing still works for this document.
        }
        set({ width: clamped });
      },
    };
  });
}

export const useRightSidebarStore = createRightSidebarStore();

// Store ownership outlives a particular panel. Clear PHI even when no panel is mounted.
onTenantChange(() => {
  const state = useRightSidebarStore.getState();
  state.updateEnvironment({ ...state.environment, ownerScope: null });
});
